import React, { useState } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'

import { mapLanguageToComponentProps } from 'i18n'
import { I18nComponentType } from 'i18n-types'
import { Notification } from '../modal/notification'
import { Body, ExternalLink, Subtitle } from '../typography'
import { ALL_COOKIES, CookieType, useCookieContext } from './context'
import { Checkbox } from '../checkbox'
import { StyledCheckbox } from '../checkbox/base-checkbox'

const ManageSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.color.grey20};
  padding-top: ${({ theme }) => theme.space.s16};
  margin-top: ${({ theme }) => theme.space.s16};

  ${StyledCheckbox} {
    margin-top: ${({ theme }) => theme.space.s8};
  }
`

const BoldLabel = styled.span`
  font-weight: 600;
`

const CookieCheckbox = styled(Checkbox)`
  align-items: flex-start;

  &:not(:last-of-type) {
    margin-bottom: ${({ theme }) => theme.space.s24};
  }
`

const CookieConsentModalFC: React.FC<{ i18n: I18nComponentType }> = ({
  i18n,
}) => {
  const [isManageCookiesOpen, setManageCookiesOpen] = useState(false)
  const [checkedCookies, setCheckedCookies] = useState([CookieType.Necessary])
  const { showCookieConsent, setAllowedCookies } = useCookieContext()

  const toggleCookieCheckbox = (cookie: CookieType) => {
    const index = checkedCookies.indexOf(cookie)
    if (index > -1) {
      setCheckedCookies([
        ...checkedCookies.slice(0, index),
        ...checkedCookies.slice(index + 1),
      ])
    } else {
      setCheckedCookies([...checkedCookies, cookie])
    }
  }

  return (
    <Notification
      id="cookie-consent-notification"
      icon="cookie"
      isOpen={!!showCookieConsent}
      showCloseButton={false}
      onClose={() => {}}
      primaryButton={
        isManageCookiesOpen
          ? {
              icon: 'cookie',
              text: i18n.cookieConsentModal.confirmChoices,
              onClick: () => {
                setAllowedCookies(checkedCookies)
              },
            }
          : {
              icon: 'cookie',
              text: i18n.cookieConsentModal.acceptAll,
              onClick: () => {
                setAllowedCookies(ALL_COOKIES)
              },
            }
      }
      secondaryButton={
        isManageCookiesOpen
          ? {
              text: i18n.cookieConsentModal.acceptAll,
              onClick: () => {
                setAllowedCookies(ALL_COOKIES)
              },
            }
          : {
              icon: 'settings',
              text: i18n.cookieConsentModal.manageCookies,
              onClick: () => setManageCookiesOpen(true),
            }
      }
    >
      <Subtitle fontWeight={600}>
        {i18n.cookieConsentModal.title}
      </Subtitle>
      <Body color="grey90" whiteSpace="pre-line">
        {i18n.cookieConsentModal.body}
      </Body>
      <Body mb="s16">
        <ExternalLink
          href={i18n.cookieConsentModal.learnMoreLink}
          target="_blank"
        >
          {i18n.cookieConsentModal.learnMoreLinkText}
        </ExternalLink>
      </Body>
      {isManageCookiesOpen && (
        <ManageSection>
          <Body fontWeight={600}>{i18n.cookieConsentModal.manageTitle}</Body>
          <CookieCheckbox
            disabled
            onChange={() => {
              /* checkbox always disabled, no need for onChange */
            }}
            value
            label={
              <>
                <BoldLabel>
                  {i18n.cookieConsentModal.necessaryCookiesTitle}
                </BoldLabel>{' '}
                <span>
                  {i18n.cookieConsentModal.necessaryCookiesBody}
                </span>
              </>
            }
          />
          <CookieCheckbox
            onChange={() => toggleCookieCheckbox(CookieType.Performance)}
            value={checkedCookies.includes(CookieType.Performance)}
            label={
              <>
                <BoldLabel>
                  {i18n.cookieConsentModal.performanceCookiesTitle}
                </BoldLabel>{' '}
                <span>
                  {i18n.cookieConsentModal.performanceCookiesBody}
                </span>
              </>
            }
          />
          <CookieCheckbox
            onChange={() => toggleCookieCheckbox(CookieType.Marketing)}
            value={checkedCookies.includes(CookieType.Marketing)}
            label={
              <>
                <BoldLabel>
                  {i18n.cookieConsentModal.marketingCookiesTitle}
                </BoldLabel>{' '}
                <span>
                  {i18n.cookieConsentModal.marketingCookiesBody}
                </span>
              </>
            }
          />
          <CookieCheckbox
            onChange={() => toggleCookieCheckbox(CookieType.Functional)}
            value={checkedCookies.includes(CookieType.Functional)}
            label={
              <>
                <BoldLabel>
                  {i18n.cookieConsentModal.functionalCookiesTitle}
                </BoldLabel>{' '}
                <span>
                  {i18n.cookieConsentModal.functionalCookiesBody}
                </span>
              </>
            }
          />
        </ManageSection>
      )}
    </Notification>
  )
}

export const CookieConsentModal = connect<{
  i18n: I18nComponentType
}>(mapLanguageToComponentProps())(CookieConsentModalFC)
